import React, { useState } from 'react';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { Button } from '@/shared/ui/Button';
import { useToast } from '@/shared/ui/Toast';
import { campaignsApi, type CampaignRec, type MarketingCampaign } from '../api/campaignsApi';
import { PNBadge, ExtBadge } from './CampaignBadges';
import { AutopilotGuard } from './AutopilotGuard';
import { CampaignCard } from './CampaignCard';
import type { ExternalCampaignData } from './externalCampaignData';
import { Scale } from 'lucide-react';
import { useTriggerAdsSync } from '../hooks/useTriggerAdsSync';
import { SyncNowButton } from './SyncNowButton';
import { CTABanner, PageHeader } from '@/shared/ui';
import { integrationsApi } from '@/modules/settings/api/integrationsApi';
import { integrationKeys } from '@/modules/settings/queryKeys';
import styles from './ActiveCampaignsView.module.css';

/** "just now" / "3h ago" / "2d ago" from an ISO timestamp. */
function syncedAgo(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const mins = (Date.now() - new Date(iso).getTime()) / 60000;
  if (!Number.isFinite(mins) || mins < 0) return null;
  if (mins < 2) return 'just now';
  if (mins < 60) return `${Math.round(mins)}m ago`;
  const hrs = mins / 60;
  if (hrs < 48) return `${Math.round(hrs)}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

const MAX_SYNC_MSG = 160;

/** Cap a long API message; the full text stays in the title tooltip. */
function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

interface Props {
  pnCampaigns: MarketingCampaign[];
  externalCampaigns: ExternalCampaignData[];
  recommendedCampaigns?: MarketingCampaign[];
  /** True while the campaign_recommendation workflow is running for this location. */
  isGeneratingRecs?: boolean;
  onReviewRecommendations?: () => void;
}

interface RebalanceRow {
  campaignId: string;
  name: string;
  variant: 'pn' | 'external';
  current: number;
}

/**
 * Which of the three list sections a campaign belongs to, by serving status.
 * Paused/ended/removed → Paused; live/active → Active; anything pre-launch
 * (recommended, ready for review, approved, launching, draft) → Proposed.
 */
function statusGroup(status: string): 'proposed' | 'active' | 'paused' {
  const s = (status ?? '').toLowerCase();
  if (['paused', 'ended', 'removed'].includes(s)) return 'paused';
  if (['live', 'active'].includes(s)) return 'active';
  return 'proposed';
}

function approveLabelForType(type: string | null | undefined): string {
  switch (type) {
    case 'budget_increase':
      return 'Approve Increase';
    case 'budget_decrease':
      return 'Approve Decrease';
    case 'pause':
      return 'Approve Pause';
    case 'new_headlines':
      return 'Approve Headlines';
    case 'add_keywords':
      return 'Approve Keywords';
    case 'add_negatives':
      return 'Approve Negatives';
    case 'adjust_schedule':
      return 'Approve Schedule';
    case 'adjust_geo':
      return 'Approve Geo';
    case 'new_creative':
      return 'Approve Creative';
    default:
      return 'Approve';
  }
}

/**
 * Phase C — active campaigns view + inline budget rebalance drawer.
 */
export function ActiveCampaignsView({
  pnCampaigns,
  externalCampaigns,
  recommendedCampaigns = [],
  isGeneratingRecs = false,
  onReviewRecommendations,
}: Props) {
  const navigate = useNavigate();
  const { currentLocation } = useLocation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [rebalanceOpen, setRebalanceOpen] = useState(false);
  const { syncNow, syncing } = useTriggerAdsSync();

  // One sync-status line per connected ad platform, so a failure on one is
  // never hidden behind a newer success on the other.
  const googleStatusQuery = useQuery({
    queryKey: integrationKeys.googleAdsStatus(currentLocation?.id ?? ''),
    queryFn: () => integrationsApi.googleAds.getStatus().then((r) => r.data),
    enabled: !!currentLocation?.id,
    staleTime: 30_000,
  });
  const metaStatusQuery = useQuery({
    queryKey: integrationKeys.metaAdsStatus(currentLocation?.id ?? ''),
    queryFn: () => integrationsApi.metaAds.getStatus(currentLocation?.id).then((r) => r.data),
    enabled: !!currentLocation?.id,
    staleTime: 30_000,
  });
  const syncLines = [
    { label: 'Google', status: googleStatusQuery.data },
    { label: 'Meta', status: metaStatusQuery.data },
  ].flatMap(({ label, status }) => {
    if (!status || !(status.connected || status.status === 'error')) return [];
    const failed = status.status === 'error' || !!status.lastSyncError;
    const warnings = status.lastSyncWarnings ?? [];
    return [
      {
        label,
        ago: syncedAgo(status.lastSyncAt),
        error: failed ? status.lastSyncError || 'unknown error' : null,
        warnings,
      },
    ];
  });
  const [edits, setEdits] = useState<Record<string, string>>({});

  const pnRows: RebalanceRow[] = pnCampaigns.map((c) => {
    const brief = (c.brief ?? {}) as { monthlyBudget?: number };
    const monthly = brief.monthlyBudget ?? 0;
    return {
      campaignId: c.id,
      name: c.name,
      variant: 'pn',
      current: monthly,
    };
  });

  const extRows: RebalanceRow[] = externalCampaigns
    .filter((c) => c.status.toUpperCase() === 'ACTIVE')
    .map((c) => {
      const monthly = (c.dailyBudget ?? 0) * 30;
      return {
        campaignId: c.marketingCampaignId ?? c.id,
        name: c.name,
        variant: 'external' as const,
        current: monthly,
      };
    });

  const allRows: RebalanceRow[] = [...pnRows, ...extRows];

  const openRebalance = () => {
    const initial: Record<string, string> = {};
    allRows.forEach((r) => {
      initial[r.campaignId] = String(Math.round(r.current));
    });
    setEdits(initial);
    setRebalanceOpen(true);
  };

  const locationCampaignRecsQuery = useQuery({
    queryKey: ['location-campaign-recs', currentLocation?.id],
    queryFn: () => campaignsApi.overview.locationCampaignRecs(currentLocation!.id),
    enabled: !!currentLocation?.id,
  });

  const locationConfigQuery = useQuery({
    queryKey: ['location-ads-config', currentLocation?.id],
    queryFn: () => campaignsApi.autopilot.getConfig(currentLocation!.id),
    enabled: !!currentLocation?.id,
  });
  const isGuided = locationConfigQuery.data?.data?.campaignApprovalPreference === 'guided';

  const allLocationRecs = locationCampaignRecsQuery.data?.data ?? [];

  const recsByCampaignId = new Map<string, CampaignRec>();
  for (const rec of allLocationRecs) {
    if (rec.marketingCampaignId && !recsByCampaignId.has(rec.marketingCampaignId)) {
      recsByCampaignId.set(rec.marketingCampaignId, rec);
    }
  }

  const priorityOrder = { high: 0, medium: 1, low: 2 } as const;
  const pendingRecs = allLocationRecs
    .filter((r) => r.status === 'pending')
    .sort(
      (a, b) =>
        (priorityOrder[a.priority ?? 'medium'] ?? 1) - (priorityOrder[b.priority ?? 'medium'] ?? 1),
    )
    .slice(0, 3);

  const approveRecMut = useMutation({
    mutationFn: ({ campaignId, recId }: { campaignId: string; recId: string }) =>
      campaignsApi.approveCampaignRec(campaignId, recId),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['location-campaign-recs'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-recs', vars.campaignId] });
      showToast('Recommendation approved', 'success');
    },
  });

  const dismissRecMut = useMutation({
    mutationFn: ({ campaignId, recId }: { campaignId: string; recId: string }) =>
      campaignsApi.dismissCampaignRec(campaignId, recId),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['location-campaign-recs'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-recs', vars.campaignId] });
    },
  });

  const rebalanceMutation = useMutation({
    mutationFn: () =>
      campaignsApi.autopilot.rebalance({
        locationId: currentLocation!.id,
        campaignBudgets: allRows.map((r) => ({
          campaignId: r.campaignId,
          monthlyBudget: Math.max(0, Math.round(Number(edits[r.campaignId] ?? r.current) || 0)),
        })),
      }),
    onSuccess: () => {
      showToast('Budgets updated — applying within 24h.', 'success');
      setRebalanceOpen(false);
      queryClient.invalidateQueries({ queryKey: ['marketing-campaigns'] });
    },
    onError: (err: unknown) => {
      showToast((err as Error).message ?? 'Rebalance failed', 'error');
    },
  });

  return (
    <div className={styles.wrap}>
      <PageHeader
        className={styles.pageHeader}
        title="Campaigns"
        subtitle={isGuided ? 'Guided mode' : 'Autopilot mode'}
        actions={
          <>
            {syncLines.length > 0 && (
              <div className={styles.syncLines}>
                {syncLines.map((l) => (
                  <span key={l.label} className={styles.syncLine}>
                    {l.label} · {syncing ? 'syncing…' : l.ago ? `last synced ${l.ago}` : 'not synced yet'}
                    {l.error && (
                      <span className={styles.syncError} title={l.error}>
                        {' '}
                        · Sync failed: {truncate(l.error, MAX_SYNC_MSG)}
                      </span>
                    )}
                    {l.warnings.length > 0 && (
                      <span className={styles.syncWarning} title={l.warnings.join('; ')}>
                        {' '}
                        · {l.warnings.length} {l.warnings.length === 1 ? 'warning' : 'warnings'}:{' '}
                        {truncate(l.warnings.join('; '), MAX_SYNC_MSG)}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            )}
            <SyncNowButton syncing={syncing} onSync={syncNow} />
            <AutopilotGuard>
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => (rebalanceOpen ? setRebalanceOpen(false) : openRebalance())}
              >
                <Scale size={14} aria-hidden /> Rebalance budgets
              </Button>
            </AutopilotGuard>
          </>
        }
      />

      {isGeneratingRecs && (
        <div className={styles.workflowCard}>
          <div className={styles.workflowSpinner} />
          <div className={styles.workflowTitle}>Generating Campaign Recommendations</div>
          <div className={styles.workflowSubtext}>
            Analyzing your strategy and practice data to create campaign briefs. Check back in 5–10
            minutes.
          </div>
        </div>
      )}

      {recommendedCampaigns.length > 0 && (
        <CTABanner
          title={`${recommendedCampaigns.length} new campaign${
            recommendedCampaigns.length === 1 ? '' : 's'
          } recommended`}
          body="Based on your latest strategy. Review and approve to get them launched."
          actions={[
            {
              label: `Review ${
                recommendedCampaigns.length === 1 ? 'recommendation' : 'recommendations'
              } →`,
              onClick: () => onReviewRecommendations?.(),
              variant: 'primary',
            },
          ]}
        />
      )}

      {rebalanceOpen && (
        <div className={styles.rebalanceDrawer}>
          <div className={styles.rebalanceTitle}>Budget rebalance</div>
          <p className={styles.rebalanceBody}>Edit monthly budgets. Changes apply within 24h.</p>
          <div className={styles.rebalanceTable}>
            <div className={styles.rebalanceHead}>
              <span>Campaign</span>
              <span>Monthly budget (Approx)</span>
            </div>
            {allRows.map((r) => (
              <div key={r.campaignId} className={styles.rebalanceRow}>
                <div className={styles.rebalanceName}>
                  {r.variant === 'pn' ? <PNBadge /> : <ExtBadge />}
                  <span>{r.name}</span>
                </div>
                <div className={styles.rebalanceInputWrap}>
                  <span className={styles.rebalanceInputPrefix}>$</span>
                  <input
                    type="number"
                    min={0}
                    step={50}
                    className={styles.rebalanceInput}
                    value={edits[r.campaignId] ?? String(Math.round(r.current))}
                    onChange={(e) =>
                      setEdits((prev) => ({ ...prev, [r.campaignId]: e.target.value }))
                    }
                  />
                </div>
              </div>
            ))}
          </div>
          <div className={styles.rebalanceActions}>
            <Button
              size="sm"
              onClick={() => rebalanceMutation.mutate()}
              isLoading={rebalanceMutation.isPending}
            >
              Save
            </Button>
            <Button variant="tertiary" size="sm" onClick={() => setRebalanceOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {pendingRecs.length > 0 && (
        <div className={styles.reviewCard}>
          <div className={styles.reviewTitle}>30-Day Performance Review</div>
          <p className={styles.reviewBody}>{pendingRecs.map((r) => r.title).join('. ')}.</p>
        </div>
      )}

      {(() => {
        // One list, grouped by serving status so the three kinds don't blur
        // together: Proposed (not yet live — PN strategy recommendations and
        // imported "ready for review" campaigns, awaiting a decision), Active
        // (serving) and Paused.
        const items = [
          ...recommendedCampaigns.map((c) => ({
            group: statusGroup(c.status),
            node: renderPnCard(c),
          })),
          ...pnCampaigns.map((c) => ({ group: statusGroup(c.status), node: renderPnCard(c) })),
          ...externalCampaigns.map((c) => ({
            group: statusGroup(c.status),
            node: renderExternalCard(c),
          })),
        ];
        const groups: { key: 'proposed' | 'active' | 'paused'; title: string; dimmed?: boolean }[] =
          [
            { key: 'proposed', title: 'Proposed' },
            { key: 'active', title: 'Active' },
            { key: 'paused', title: 'Paused', dimmed: true },
          ];
        return groups.map(({ key, title, dimmed }) => {
          const groupItems = items.filter((i) => i.group === key);
          if (groupItems.length === 0) return null;
          return (
            <section
              key={key}
              className={clsx(styles.campaignGroup, dimmed && styles.campaignGroupDimmed)}
            >
              <div className={styles.campaignGroupHeader}>
                {title}
                <span className={styles.campaignGroupCount}>{groupItems.length}</span>
              </div>
              {groupItems.map((i) => i.node)}
            </section>
          );
        });
      })()}
    </div>
  );

  function renderPnCard(c: MarketingCampaign) {
    const brief = (c.brief ?? {}) as { monthlyBudget?: number; offer?: string };
    const totals = (c.campaigns ?? []).reduce(
      (acc, d) => ({
        impressions: acc.impressions + (d.impressions ?? 0),
        clicks: acc.clicks + (d.clicks ?? 0),
        conversions: acc.conversions + (d.conversions ?? 0),
        spend: acc.spend + (d.spend ?? 0),
      }),
      { impressions: 0, clicks: 0, conversions: 0, spend: 0 },
    );
    const topRec = recsByCampaignId.get(c.id);
    const launches = (c.campaigns ?? [])
      .filter((d): d is typeof d & { launchedAt: string } => !!d.launchedAt)
      .map((d) => ({ channel: d.channel ?? d.platform, launchedAt: d.launchedAt }));
    return (
      <CampaignCard
        key={c.id}
        variant="pn"
        name={c.name}
        objective={c.objective}
        status={c.status}
        monthlyBudget={brief.monthlyBudget ?? 0}
        spend={totals.spend}
        impressions={totals.impressions}
        clicks={totals.clicks}
        conversions={totals.conversions}
        launches={launches}
        onClick={() => navigate(`/campaigns/${c.id}`)}
        recSlot={renderRecSlot(c.id, topRec)}
      />
    );
  }

  function renderExternalCard(c: ExternalCampaignData) {
    const topRec = c.marketingCampaignId ? recsByCampaignId.get(c.marketingCampaignId) : undefined;
    const campaignId = c.marketingCampaignId ?? c.id;
    const monthlyBudget = c.dailyBudget ? c.dailyBudget * 30 : 0;
    return (
      <CampaignCard
        key={c.id}
        variant="external"
        name={c.name}
        objective={c.objective}
        status={c.status}
        monthlyBudget={monthlyBudget}
        spend={c.spend}
        impressions={c.impressions}
        clicks={c.clicks}
        conversions={c.conversions}
        cpa={c.cpa}
        launches={c.launches}
        removed={c.removed}
        onClick={
          c.marketingCampaignId ? () => navigate(`/campaigns/${c.marketingCampaignId}`) : undefined
        }
        recSlot={renderRecSlot(campaignId, topRec)}
      />
    );
  }

  function renderRecSlot(campaignId: string, topRec: CampaignRec | undefined) {
    if (!topRec) return undefined;
    return (
      <div className={styles.cardRec} onClick={(e) => e.stopPropagation()} role="presentation">
        <div className={styles.cardRecLabel}>Recommendation</div>
        <div className={styles.cardRecTitle}>{topRec.title}</div>
        <div className={styles.cardRecDesc}>{topRec.description}</div>
        <AutopilotGuard>
          <div className={styles.cardRecActions}>
            <Button
              size="sm"
              onClick={() => approveRecMut.mutate({ campaignId, recId: topRec.id })}
              isLoading={approveRecMut.isPending && approveRecMut.variables?.recId === topRec.id}
            >
              {topRec.actionLabel ??
                approveLabelForType(topRec.category ?? topRec.actionData?.type)}
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => dismissRecMut.mutate({ campaignId, recId: topRec.id })}
              isLoading={dismissRecMut.isPending && dismissRecMut.variables?.recId === topRec.id}
            >
              Dismiss
            </Button>
          </div>
        </AutopilotGuard>
      </div>
    );
  }
}
